import { useMemo } from 'react'
import { copyToClipboard } from '@/lib/clipboard'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { ExternalLink, Share2, Scale } from 'lucide-react'
import PageContainer from '@/components/layout/PageContainer'
import SubpageHero from '@/components/common/SubpageHero'
import Button from '@/components/ui/Button'
import Spinner from '@/components/ui/Spinner'
import EmptyState from '@/components/common/EmptyState'
import ComparisonTable from '@/components/compare/ComparisonTable'
import { useFacility } from '@/features/facility/api'
import { ROUTES } from '@/lib/constants'
import type { FacilityDetail } from '@/types/facility'

/**
 * 시설 비교 페이지 (MVP).
 *
 * URL: /compare?ids=1,2,3
 * 찜 목록에서 2~3개 선택 후 이 페이지로 이동.
 * 각 시설 데이터를 GET /facilities/{id} 로 개별 조회 후
 * 클라이언트 사이드에서 비교합니다.
 */
export default function ComparePage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // URL에서 시설 ID 파싱
  const facilityIds = useMemo(() => {
    const idsParam = searchParams.get('ids') ?? ''
    return idsParam
      .split(',')
      .map((id) => parseInt(id, 10))
      .filter((id) => !isNaN(id) && id > 0)
      .slice(0, 3)
  }, [searchParams])

  // 각 시설 데이터 조회 (최대 3개)
  const facility1 = useFacility(facilityIds[0] ?? 0)
  const facility2 = useFacility(facilityIds[1] ?? 0)
  const facility3 = useFacility(facilityIds[2] ?? 0)

  const queries = [facility1, facility2, facility3].slice(0, facilityIds.length)

  const isLoading = queries.some((q) => q.isLoading)
  const isError = queries.some((q) => q.isError)
  const facilities = queries
    .map((q) => q.data)
    .filter((d): d is FacilityDetail => d != null)

  // ID 없음
  if (facilityIds.length < 2) {
    return (
      <PageContainer>
        <EmptyState
          message="비교할 시설을 선택해주세요"
          description="관심 시설에서 2~3개의 시설을 선택하세요"
          actionLabel="관심 시설로 이동"
          onAction={() => navigate(ROUTES.BOOKMARKS)}
        />
      </PageContainer>
    )
  }

  // 로딩
  if (isLoading) {
    return (
      <PageContainer>
        <div className="flex flex-col items-center gap-3 py-16">
          <Spinner size="lg" />
          <p className="text-body text-cb-gray-500">
            시설 정보를 불러오고 있어요...
          </p>
        </div>
      </PageContainer>
    )
  }

  // 에러
  if (isError || facilities.length < 2) {
    return (
      <PageContainer>
        <EmptyState
          message="시설 정보를 불러오지 못했어요"
          description="잠시 후 다시 시도해주세요."
          actionLabel="관심 시설로 이동"
          onAction={() => navigate(ROUTES.BOOKMARKS)}
        />
      </PageContainer>
    )
  }

  return (
    <>
      <SubpageHero
        title="시설 비교"
        description={`${facilities.length}개 시설을 한눈에 비교합니다`}
        icon={<Scale size={20} className="text-white" />}
      />

      <div className="px-5 py-6 lg:max-w-6xl lg:mx-auto lg:px-6 lg:py-10">
        {/* 비교 테이블 */}
        <ComparisonTable facilities={facilities} />

        {/* 시설별 상세보기 카드 */}
        <div className="mt-8 grid grid-cols-1 gap-4 lg:grid-cols-3">
          {facilities.map((facility) => (
            <button
              key={facility.id}
              onClick={() => navigate(`/facilities/${facility.id}`)}
              className="group flex items-center gap-3 rounded-2xl bg-white border border-gray-100 shadow-sm p-4 text-left hover:shadow-md hover:-translate-y-0.5 transition-all"
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-gray-900 truncate group-hover:text-cb-primary transition-colors">{facility.name}</p>
                <p className="text-xs text-gray-400 mt-0.5">{facility.address ?? facility.addressSido}</p>
              </div>
              <ExternalLink size={16} className="text-gray-300 flex-shrink-0" />
            </button>
          ))}
        </div>

        {/* 공유 + 돌아가기 */}
        <div className="mt-8 flex gap-3">
          <Button
            variant="secondary"
            size="lg"
            fullWidth
            onClick={async () => {
              const url = window.location.href
              const names = facilities.map(f => f.name).join(' vs ')
              if (navigator.share) {
                try { await navigator.share({ title: `시설 비교: ${names}`, url }) } catch {}
              } else {
                const ok = await copyToClipboard(url)
                alert(ok ? '비교 링크가 복사되었습니다' : '복사에 실패했습니다. 주소창의 링크를 직접 복사해주세요')
              }
            }}
            className="flex items-center justify-center gap-2"
          >
            <Share2 size={16} />
            비교 결과 공유
          </Button>
          <Button variant="ghost" size="lg" fullWidth onClick={() => navigate(-1)}>
            돌아가기
          </Button>
        </div>
      </div>
    </>
  )
}
